import { Injectable } from '@angular/core';
import {
  MenuItemPublicDto,
  FirstLevelMenuItemPublicDto,
  SecondLevelMenuItemPublicDto,
  ThirdLevelMenuItemPublicDto,
} from '../dtos';
import {
  MenuItem,
  FirstLevelMenuItem,
  ThirdLevelMenuItem,
  SecondLevelMenuItem,
} from '../models';

/**
 * Mapper para transformar DTOs del menú en modelos de dominio
 * Siguiendo principios de arquitectura hexagonal para separar
 * la lógica de transformación de datos externos
 */
@Injectable({
  providedIn: 'root',
})
export class MenuMapper {
  /*private readonly URL_BASE_CATEGORIES = [
    '/',
    'inicio',
    'productos',
    'todos',
  ] as const;
  private readonly URL_BASE = ['/', 'inicio', 'productos'] as const;*/

  /**
   * Transforma un array de DTOs del menú en modelos de dominio
   * @param dtos - Array de MenuItemPublicDto del API
   * @returns Array de MenuItem para uso interno
   */
  mapMenuDtosToMenuItems(dtos: MenuItemPublicDto[]): MenuItem[] {
    return dtos.map((dto) => this.mapMenuDtoToMenuItem(dto));
  }

  /**
   * Transforma un DTO del menú de primer nivel en modelo de dominio
   * @param dto - MenuItemPublicDto del API
   * @returns MenuItem para uso interno
   */
  mapMenuDtoToMenuItem(dto: MenuItemPublicDto): MenuItem {
    const pathSegments = dto.urlPath.split('/').filter(Boolean);
    // Temporal para tiendas oficiales que no respeta el estandar...
    let url: string[] = [];
    if (dto.name === 'Tiendas Oficiales') {
      url = ['/', ...pathSegments];
    } else {
      url = ['/', ...pathSegments, dto.slug];
    }

    return {
      url,
      name: dto.name,
      slug: dto.slug,
      hasViewAllLink: dto.hasViewAllLink,
      items: dto.items?.length
        ? this.mapFirstLevelMenuDtos(dto.items, url)
        : [],
    };
  }

  /**
   * Transforma DTOs del primer nivel del menú
   * @param dtos - Array de FirstLevelMenuItemPublicDto
   * @param parentUrl - URL del elemento padre
   * @returns Array de FirstLevelMenuItem
   */
  private mapFirstLevelMenuDtos(
    dtos: FirstLevelMenuItemPublicDto[],
    parentUrl: string[]
  ): FirstLevelMenuItem[] {
    return dtos.map((dto) => {
      const url = [...parentUrl, dto.slug];

      return {
        name: dto.name,
        slug: dto.slug,
        hasViewAllLink: dto.hasViewAllLink,
        url,
        items: dto.items?.length
          ? this.mapSecondLevelMenuDtos(dto.items, url)
          : [],
      };
    });
  }

  /**
   * Transforma DTOs del segundo nivel del menú
   * @param dtos - Array de SecondLevelMenuItemPublicDto
   * @param parentUrl - URL del elemento padre
   * @returns Array de SecondLevelMenuItem
   */
  private mapSecondLevelMenuDtos(
    dtos: SecondLevelMenuItemPublicDto[],
    parentUrl: string[]
  ): SecondLevelMenuItem[] {
    return dtos.map((dto) => {
      const url = [...parentUrl, dto.slug];

      return {
        name: dto.name,
        slug: dto.slug,
        hasViewAllLink: dto.hasViewAllLink,
        url,
        items: dto.items?.length
          ? this.mapThirdsLevelMenuDtos(dto.items, url)
          : [],
      };
    });
  }

  /**
   * Transforma DTOs del tercer nivel del menú
   * @param dtos - Array de ThirdLevelMenuItemPublicDto
   * @param parentUrl - URL del elemento padre
   * @returns Array de BaseMenuItem
   */
  private mapThirdsLevelMenuDtos(
    dtos: ThirdLevelMenuItemPublicDto[],
    parentUrl: string[]
  ): ThirdLevelMenuItem[] {
    return dtos.map((dto) => ({
      name: dto.name,
      slug: dto.slug,
      url: [...parentUrl, dto.slug],
    }));
  }
}
